/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Ashley Advantage finance card provider, detected from the leading digits of the account number.
 *
 * Mirrors legacy `int_ashley_payment/.../util/FinanceCardHelper.js#getFinanceCardType`, which reads
 * the `financeCardTypes` site preference (BIN regex → provider name). SF Next is Synchrony-only
 * today (see z_concora_bin_toggle.md); this helper exists so the checkout UI can hide Synchrony-
 * only affordances (e.g. the CVV field) for a Concora account — legacy parity for SSF-407.
 *
 * When the full BIN-routing infra lands (Phase 1 of the concora toggle plan), the inlined BIN
 * table below should be replaced by values sourced from the shopper config / SCAPI so ops can
 * extend the list in BM without a code deploy.
 */
export type FinanceCardType = 'Synchrony' | 'Concora';

/**
 * Genesis/Concora BIN prefixes. Legacy `financeCardTypes` uses the regex `^763[24]00\d{10}$` —
 * two 6-digit BINs, 763200 and 763400. Concora shares the APG apikey with Synchrony but does NOT
 * collect a security code, so the CVV field must be hidden for these BINs.
 */
const CONCORA_BINS = ['763200', '763400'] as const;

/**
 * Detect the finance-card provider for an Ashley Advantage account number.
 *
 * @param accountNumber Raw input from the shopper (may contain spaces or partial digits).
 * @returns `'Concora'` when the first six digits match a Concora BIN; otherwise `'Synchrony'`.
 *          Returns `'Synchrony'` for inputs shorter than 6 digits so partial input doesn't hide
 *          Synchrony affordances mid-typing — the callers additionally require a full 16-digit
 *          account before revealing the CVV field.
 */
export function detectFinanceCardType(accountNumber: string): FinanceCardType {
    const clean = accountNumber.replace(/\D/g, '');
    if (clean.length < 6) return 'Synchrony';
    const bin = clean.slice(0, 6);
    if ((CONCORA_BINS as readonly string[]).includes(bin)) return 'Concora';
    return 'Synchrony';
}
